import React, { useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Container,
  Paper,
  Card,
  CardContent,
  Chip,
  Avatar,
  Alert,
  Divider
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Dashboard as DashboardIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Analytics as AnalyticsIcon
} from "@mui/icons-material";
import Longwis1 from "./longwis1";
import Longwis18 from "./longwis18";

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#1dc39f',
    },
    secondary: {
      main: '#8ff9ab',
    },
    background: {
      default: '#f8fffe',
    },
  },
  typography: {
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: 'white',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1dc39f',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1dc39f',
          },
        },
      },
    },
  },
});

const Dashboard = () => {
  const [longwis, setLongwis] = useState(244);

  const longwisOptions = [
    { value: 244, label: "Longwis 244", description: "Data Lorong Wisata Tahun 2024" },
    { value: 1815, label: "Longwis 1815", description: "Data Lorong Wisata Tahun 2018-2015" }
  ];

  const selectedLongwis = longwisOptions.find(option => option.value === longwis);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%)",
        py: 4
      }}
    >
      <ThemeProvider theme={defaultTheme}>
        <Container maxWidth="xl">
          {/* Header Section */}
          <Paper 
            elevation={8}
            sx={{
              p: 4,
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 3,
                  boxShadow: 3
                }}
              >
                <DashboardIcon sx={{ fontSize: 35, color: 'white' }} />
              </Box>
              
              <Box>
                <Typography 
                  component="h1" 
                  variant="h4" 
                  sx={{ 
                    color: 'primary.main', 
                    fontWeight: 700,
                    mb: 0.5
                  }}
                >
                  Dashboard Admin
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  Monitoring dan Analisis Data Lorong Wisata Kota Makassar
                </Typography>
              </Box>
            </Box>

            {/* Selection Section */}
            <Card sx={{ border: '1px solid', borderColor: 'primary.light', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <AnalyticsIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Pilih Dataset Analisis
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 300, flex: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel id="longwis-select-label">Dataset Longwis</InputLabel>
                      <Select
                        labelId="longwis-select-label"
                        id="longwis-select"
                        value={longwis}
                        label="Dataset Longwis"
                        onChange={(ev) => setLongwis(ev.target.value)}
                        startAdornment={<LocationIcon color="action" sx={{ mr: 1 }} />}
                      >
                        {longwisOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Box>
                              <Typography variant="body1" fontWeight={500}>
                                {option.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {option.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {selectedLongwis && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        avatar={<Avatar sx={{ bgcolor: 'primary.main' }}><BusinessIcon /></Avatar>}
                        label={`Dataset Aktif: ${selectedLongwis.label}`}
                        color="primary"
                        variant="outlined"
                        sx={{ 
                          borderWidth: 2,
                          fontWeight: 600,
                          py: 2,
                          px: 1
                        }}
                      />
                    </Box>
                  )}
                </Box>

                <Alert 
                  severity="info" 
                  sx={{ 
                    mt: 3,
                    borderRadius: 2,
                    backgroundColor: 'rgba(29, 195, 159, 0.1)',
                    borderColor: 'primary.main'
                  }}
                >
                  <Typography variant="body2">
                    <strong>Dataset {selectedLongwis?.label}</strong> - {selectedLongwis?.description}
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Paper>

          {/* Content Section */}
          <Paper 
            elevation={8}
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              overflow: 'hidden'
            }}
          >
            {longwis === 244 ? <Longwis1 /> : <Longwis18 />}
          </Paper>
        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default Dashboard;
